import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * Maps a normalized (lowercased) URL hash token to the DOM `id` of the
 * matching in-page anchor. `cli` and `mcp` point at invisible markers at their
 * section boundaries; `getting-started` points at the
 * Getting Started section wrapper itself.
 *
 * The logged-in and logged-out home views render different components for the
 * connectors section, but both expose it under the single `connectors` id so
 * one token reaches it either way.
 *
 * Every token here is also a valid landing hash for analytics. The nav bar
 * links to `/#<token>` from `/cli` and rewrites the fragment to `#<token>`
 * when scrolling in-page, so a token (never an element id) is what a shared
 * link carries.
 */
export const HASH_TO_ANCHOR_ID: Readonly<Record<string, string>> = {
	about: 'overview',
	'your-graph': 'graph-preview',
	connectors: 'connectors',
	cli: 'getting-started-v2-cli',
	mcp: 'getting-started-v2-mcp',
	'getting-started': 'getting-started-v2',
	pricing: 'faq',
};

/**
 * True only for own keys of the map. Uses `hasOwnProperty` so inherited
 * `Object.prototype` keys (`constructor`, `toString`, etc.) are never treated
 * as valid sections.
 */
const isKnownSection = (token: string): boolean =>
	Object.prototype.hasOwnProperty.call(HASH_TO_ANCHOR_ID, token);

/**
 * Normalizes a raw `window.location.hash` into a bare, lowercased token:
 * drops the leading `#`, drops everything from the first `?` (the QR
 * `#CLI?utm_source=...` format keeps UTMs in the fragment), decodes and
 * lowercases. Returns '' when there is no usable token.
 */
export const normalizeHash = (rawHash: string): string => {
	if (!rawHash) {
		return '';
	}
	let value = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
	const queryIdx = value.indexOf('?');
	if (queryIdx !== -1) {
		value = value.slice(0, queryIdx);
	}
	try {
		value = decodeURIComponent(value);
	} catch {
		// Leave the value as-is if it is not valid percent-encoding.
	}
	return value.trim().toLowerCase();
};

/** Resolves a raw hash to a known anchor element id, or null if unknown. */
export const resolveAnchorId = (rawHash: string): string | null => {
	const token = normalizeHash(rawHash);
	if (!isKnownSection(token)) {
		return null;
	}
	if (token === 'pricing' && !fg('twg_microsite_pricing_faqs')) {
		return null;
	}
	return HASH_TO_ANCHOR_ID[token];
};

/**
 * Returns the query-string portion after the first `?` inside the hash
 * fragment (the QR `#CLI?utm_source=...` format), or '' if there is none.
 */
export const extractHashQuery = (rawHash: string): string => {
	const queryIdx = rawHash.indexOf('?');
	return queryIdx === -1 ? '' : rawHash.slice(queryIdx + 1);
};

/**
 * One-time snapshot of the raw `window.location.hash`, taken on the first
 * call. Subsequent calls return the cached value so scroll, analytics, and
 * attribution all read a single stable landing hash even if the URL hash
 * later changes. SSR-safe (returns '').
 */
let cachedRawHash: string | null = null;

export const captureLandingHash = (): string => {
	if (cachedRawHash === null) {
		cachedRawHash = getDocument()?.location?.hash ?? '';
	}
	return cachedRawHash;
};

/** Test-only: clears the memoized snapshot. Do not call from app code. */
export const __resetLandingHashForTests = (): void => {
	cachedRawHash = null;
};

/**
 * Landing token for analytics. Only ever returns a bare token from
 * `HASH_TO_ANCHOR_ID`, never an element id; returns null when the hash is not
 * a known section, so dashboards never receive arbitrary fragments and callers
 * can pass it directly.
 */
export const getLandingHashForAnalytics = (): string | null => {
	const token = normalizeHash(captureLandingHash());
	if (!isKnownSection(token)) {
		return null;
	}
	if (token === 'pricing' && !fg('twg_microsite_pricing_faqs')) {
		return null;
	}
	return token;
};
